import React, { Dispatch, useMemo, useState } from 'react';
import { OrderedSet } from 'immutable';
import { useDispatch, useSelector } from 'react-redux';
import { PlusIcon, XIcon } from '@repo/icons';
import { addSourceLabel, removeSourceLabel } from '@repo/redux/actions/ui';
import { selectCustomSourceLabels } from '@repo/redux/reducers/ui/sourceLabels';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import styles from './LabelEditor.module.scss';
import { ID } from '@repo/utils/types';
import { selectContactSources } from '@repo/redux/reducers/data/sources';
import { defaultLabelOptionsForSource } from '@repo/utils/models/Contact';
import SourceLabelOption from '../SourceLabelOption';
import { updateSourceLabel } from '@repo/redux/actions/sources';

export default function SourceLabelEditor({
  contactID,
  sourceID,
  editing,
  setEditing,
  value,
  setValue,
}: {
  contactID: ID;
  sourceID: ID;
  editing: boolean;
  setEditing: Dispatch<boolean>;
  value: string;
  setValue: Dispatch<string>;
}) {
  const dispatch = useDispatch();
  const sources = useSelector(selectContactSources(contactID));
  const source = sources.items.get(String(sourceID));
  const storedCustomLabels = useSelector(selectCustomSourceLabels(source.type));
  const [removedUnstoredCustomLabel, setRemovedUnstoredCustomLabel] = useState(false);
  const defaultLabels = defaultLabelOptionsForSource(source.type);
  const unstoredCustomLabel: OrderedSet<string> = useMemo(() => {
    if (
      source.label &&
      !defaultLabels.includes(source.label) &&
      !storedCustomLabels.has(source.label)
    ) {
      return OrderedSet([source.label]);
    } else {
      return OrderedSet();
    }
  }, [removedUnstoredCustomLabel]);
  const customLabels = unstoredCustomLabel.union(storedCustomLabels);

  const removeSourceLabelFromStorage = (label: string) => {
    dispatch(removeSourceLabel(source.type as 'phone' | 'email', label));

    if (source.label === label) {
      dispatch(updateSourceLabel({ contactID, sourceID, label: null }));

      if (unstoredCustomLabel.has(label)) {
        setRemovedUnstoredCustomLabel(true);
      }
    }
  };

  const selectLabel = (label: string) => {
    dispatch(updateSourceLabel({ contactID, sourceID, label }));
  };

  return (
    <div>
      <TransitionGroup>
        {customLabels
          .valueSeq()
          .toArray()
          .map((label) => (
            <CSSTransition
              key={label}
              timeout={150}
              classNames={{
                enter: styles.customLabelOptionEnter,
                enterActive: styles.customLabelOptionEnterActive,
                exit: styles.customLabelOptionExit,
                exitActive: styles.customLabelOptionExitActive,
              }}
            >
              <div className={styles.customLabelOption} onClick={() => selectLabel(label)}>
                <SourceLabelOption selected={source.label === label}>
                  <span style={{ flex: 1 }}>{label}</span>
                  <button
                    className={styles.customLabelOptionRemoveBtn}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSourceLabelFromStorage(label);
                    }}
                  >
                    <XIcon width={11} height={11} />
                  </button>
                </SourceLabelOption>
              </div>
            </CSSTransition>
          ))}
      </TransitionGroup>

      {!editing && (
        <button className={styles.createLabelBtn} onClick={() => setEditing(true)}>
          <PlusIcon width="10" height="10" />
          Create Label
        </button>
      )}

      {editing && (
        <input
          type="text"
          className={styles.labelInput}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          placeholder="Enter label"
          size={10}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              dispatch(addSourceLabel(source.type as 'email' | 'phone', value));
              selectLabel(value);
              setValue('');
              setEditing(false);
            }

            if (e.key === 'Escape') {
              e.preventDefault();
              setValue('');
              setEditing(false);
            }

            // this prevents default menu behavior that messes with focus states
            e.stopPropagation();
          }}
        />
      )}
    </div>
  );
}
